import React, { useState, useEffect } from 'react';
import { DollarSign, ArrowRight, RefreshCw } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент конвертера валют для быстрой конвертации между валютами
 */
const CurrencyConverter = ({ 
  currencyRates,
  defaultFromCurrency = 'USD',
  supportedCurrencies 
}) => {
  const [amount, setAmount] = useState('1000');
  const [fromCurrency, setFromCurrency] = useState(defaultFromCurrency);
  const [toCurrency, setToCurrency] = useState('RUB');
  const [convertedAmount, setConvertedAmount] = useState('');
  const [reverseConvertedAmount, setReverseConvertedAmount] = useState('');

  // Обновление конвертации при изменении входных данных
  useEffect(() => {
    if (amount && !isNaN(parseFloat(amount))) {
      const numAmount = parseFloat(amount.replace(',', '.'));
      
      // Конвертация из выбранной валюты в рубли
      if (fromCurrency === 'RUB' && toCurrency !== 'RUB') {
        if (currencyRates[toCurrency]) {
          const result = numAmount / currencyRates[toCurrency];
          setConvertedAmount(formatNumber(result, 2));
          setReverseConvertedAmount(formatNumber(1 / (result / numAmount), 2));
        }
      } 
      // Конвертация из валюты в рубли
      else if (fromCurrency !== 'RUB' && toCurrency === 'RUB') {
        if (currencyRates[fromCurrency]) {
          const result = numAmount * currencyRates[fromCurrency];
          setConvertedAmount(formatNumber(result, 2));
          setReverseConvertedAmount(formatNumber(1 / (result / numAmount), 2));
        }
      } 
      // Конвертация между двумя валютами через рубль
      else if (fromCurrency !== 'RUB' && toCurrency !== 'RUB') {
        if (currencyRates[fromCurrency] && currencyRates[toCurrency]) {
          const amountInRub = numAmount * currencyRates[fromCurrency];
          const result = amountInRub / currencyRates[toCurrency];
          setConvertedAmount(formatNumber(result, 2));
          setReverseConvertedAmount(formatNumber(1 / (result / numAmount), 2));
        }
      }
      // Если обе валюты - рубли
      else {
        setConvertedAmount(formatNumber(numAmount, 2));
        setReverseConvertedAmount(formatNumber(numAmount, 2));
      }
    } else {
      setConvertedAmount('');
      setReverseConvertedAmount('');
    }
  }, [amount, fromCurrency, toCurrency, currencyRates]);

  // Обработчик изменения суммы
  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
      setAmount(value);
    }
  };

  // Поменять валюты местами
  const handleSwapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  };

  // Получение символа валюты
  const getCurrencySymbol = (currency) => {
    const foundCurrency = supportedCurrencies.find(c => c.id === currency);
    return foundCurrency ? foundCurrency.symbol : currency === 'RUB' ? '₽' : currency;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm mb-6">
      <div className="flex flex-col md:flex-row gap-4 items-center">
        {/* Сумма для конвертации */}
        <div className="w-full md:w-1/3">
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Сумма
          </label>
          <div className="relative">
            <input
              type="text"
              id="amount"
              value={amount}
              onChange={handleAmountChange}
              className="form-input w-full pr-8"
              placeholder="Введите сумму"
            />
          </div>
        </div>

        {/* Исходная валюта */}
        <div className="w-full md:w-1/4">
          <label htmlFor="fromCurrency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Из валюты
          </label>
          <select
            id="fromCurrency"
            value={fromCurrency}
            onChange={(e) => setFromCurrency(e.target.value)}
            className="form-select w-full"
          >
            <option value="RUB">Российский рубль (₽)</option>
            {supportedCurrencies.map((currency) => (
              <option key={`from-${currency.id}`} value={currency.id}>
                {currency.name} ({currency.symbol})
              </option>
            ))}
          </select>
        </div>

        {/* Кнопка обмена валютами */}
        <div className="flex items-center justify-center w-8 h-8">
          <button
            type="button"
            onClick={handleSwapCurrencies}
            className="p-1 rounded-full text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/30 transition-colors"
            aria-label="Поменять валюты местами"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
        </div>

        {/* Целевая валюта */}
        <div className="w-full md:w-1/4">
          <label htmlFor="toCurrency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            В валюту
          </label>
          <select
            id="toCurrency"
            value={toCurrency}
            onChange={(e) => setToCurrency(e.target.value)}
            className="form-select w-full"
          >
            <option value="RUB">Российский рубль (₽)</option>
            {supportedCurrencies.map((currency) => (
              <option key={`to-${currency.id}`} value={currency.id}>
                {currency.name} ({currency.symbol})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Результаты конвертации */}
      <div className="mt-6 bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800">
        <div className="flex flex-col md:flex-row justify-between gap-4">
          {/* Результат прямой конвертации */}
          <div className="text-center md:text-left">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              {amount || '0'} {getCurrencySymbol(fromCurrency)} =
            </div>
            <div className="text-xl font-bold text-indigo-700 dark:text-indigo-300">
              {convertedAmount || '0'} {getCurrencySymbol(toCurrency)}
            </div>
          </div>

          {/* Обратный курс */}
          <div className="text-center md:text-right">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              1 {getCurrencySymbol(fromCurrency)} =
            </div>
            <div className="text-lg font-medium text-indigo-600 dark:text-indigo-400">
              {fromCurrency !== toCurrency 
                ? `${reverseConvertedAmount ? (parseFloat(amount) / parseFloat(convertedAmount.replace(/\s/g, ''))).toFixed(4) : '0'} ${getCurrencySymbol(toCurrency)}`
                : `1 ${getCurrencySymbol(toCurrency)}`
              }
            </div>
          </div>
        </div>
        
        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 flex items-center">
          <RefreshCw className="w-3 h-3 mr-1 inline" />
          Курсы валют обновлены {new Date().toLocaleDateString('ru-RU')}
        </div>
      </div>
    </div>
  );
};

export default CurrencyConverter;